import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import { Calendar, ArrowLeft, Clock, Share2, Bookmark, Megaphone } from 'lucide-react';
import { api } from '../utils/api';

export const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadNews = async () => {
      try {
        const res = await api.news.getAll(true);
        setNews(res.data);
      } catch (error) {
        console.error('Error loading news:', error);
      } finally {
        setLoading(false);
      }
    };
    loadNews();
  }, []);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="w-12 h-12 border border-white/10 rounded-full flex items-center justify-center animate-spin">
          <div className="w-2 h-2 rounded-full bg-[#C6FF00]"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-black text-white pt-20 selection:bg-[#C6FF00] selection:text-black">
      {/* Header */}
      <section className="relative py-24 border-b border-white/5 overflow-hidden grid-bg">
        <div className="absolute inset-0 mesh-gradient opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="text-[#C6FF00] font-black tracking-[0.4em] uppercase text-[10px] mb-4 block">Archivo y Perspectivas</span>
            <h1 className="text-6xl sm:text-8xl font-black tracking-tighter mb-8 leading-none italic">
              DENTRO DE<br />
              <span className="text-gradient">BRAINS</span>
            </h1>
            <p className="text-xl text-white/50 max-w-2xl font-medium tracking-tight">
              Inmersiones profundas en la tecnología, filosofía y evolución de nuestro ecosistema digital.
            </p>
          </motion.div>
        </div>
      </section>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        {news.length === 0 ? (
          <div className="text-center text-white/20 py-32 border border-white/5 rounded-[3rem] bg-white/[0.02]">
            <p className="text-sm font-black uppercase tracking-widest">Señal perdida. No se encontraron actualizaciones.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            {news.map((item, index) => (
              <motion.article
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className="group flex flex-col"
              >
                <Link to={`/noticias/${item.id}`} className="block overflow-hidden rounded-[2.5rem] bg-black border border-white/5 mb-8 aspect-[16/10] relative group-hover:border-[#C6FF00]/30 transition-all duration-500">
                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-1000 ease-out opacity-60 group-hover:opacity-100"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center mesh-gradient opacity-10">
                      <Clock size={48} className="text-white/20" />
                    </div>
                  )}
                  <div className="absolute top-6 left-6">
                    <span className="px-4 py-1.5 rounded-full bg-black/80 backdrop-blur-xl border border-white/10 text-[9px] text-white/80 font-black uppercase tracking-[0.2em]">
                      Despliegue
                    </span>
                  </div>
                </Link>
                
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-[#C6FF00] font-black text-[10px] uppercase tracking-widest">
                    {new Date(item.created_at).toLocaleDateString('es-ES', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-white/20"></span>
                  <span className="text-white/30 text-[10px] font-black uppercase tracking-widest">{Math.ceil(item.content?.length / 1000) || 4} min lect</span>
                </div>
                
                <h3 className="text-3xl sm:text-4xl font-black text-white mb-6 group-hover:text-[#C6FF00] transition-colors line-clamp-2 leading-[1.1] tracking-tighter">
                  <Link to={`/noticias/${item.id}`}>{item.title}</Link>
                </h3>
                
                <p className="text-white/40 text-lg font-medium tracking-tight mb-8 line-clamp-2 leading-relaxed">
                  {item.summary}
                </p>
                
                <Link
                  to={`/noticias/${item.id}`}
                  className="mt-auto inline-flex items-center space-x-4 text-white font-black text-xs uppercase tracking-[0.3em] group/btn transition-all"
                >
                  <span className="group-hover/btn:text-[#C6FF00] transition-colors">Leer Señal Completa</span>
                  <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center group-hover/btn:border-[#C6FF00] transition-colors">
                    <ArrowLeft size={14} className="rotate-180 group-hover/btn:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
export const NewsDetail = () => {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });
  
  useEffect(() => {
    const loadNewsDetail = async () => {
      try {
        const res = await api.news.getById(id);
        setNews(res.data);
      } catch (error) {
        console.error('Error loading news detail:', error);
      } finally {
        setLoading(false);
      }
    };
    loadNewsDetail();
    window.scrollTo(0, 0);
  }, [id]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="w-12 h-12 border border-white/10 rounded-full flex items-center justify-center animate-spin">
          <div className="w-2 h-2 rounded-full bg-[#C6FF00]"></div>
        </div>
      </div>
    );
  }
  
  if (!news) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white">
        <h2 className="text-2xl font-black mb-8 italic tracking-tighter uppercase text-white/20">SEÑAL PERDIDA</h2>
        <Link to="/noticias" className="text-[#C6FF00] font-black uppercase tracking-widest text-xs border border-[#C6FF00]/30 px-10 py-3 rounded-full hover:bg-[#C6FF00] hover:text-black transition-all">Regresar a Base</Link>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-black text-white selection:bg-[#C6FF00] selection:text-black font-medium">
      {/* Reading Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-[#C6FF00] z-[100] origin-left"
        style={{ scaleX }}
      />

      {/* Hero Header Section */}
      <section className="relative pt-40 pb-32 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 grid-bg opacity-30"></div>
        <div className="absolute inset-0 mesh-gradient opacity-20"></div>
        
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col items-center text-center"
          >
            <Link
              to="/noticias"
              className="group inline-flex items-center space-x-3 text-white/30 hover:text-[#C6FF00] transition-colors mb-16 text-[10px] font-black uppercase tracking-[0.4em] bg-white/5 px-6 py-2 rounded-full border border-white/5 hover:border-[#C6FF00]/20"
            >
              <ArrowLeft size={12} className="group-hover:-translate-x-1 transition-transform" />
              <span>Regresar al Archivo</span>
            </Link>
            
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex items-center gap-6 mb-12"
            >
              <span className="px-5 py-2 rounded-2xl bg-[#C6FF00]/5 border border-[#C6FF00]/20 text-[#C6FF00] text-[10px] font-black uppercase tracking-[0.2em] shadow-[0_0_30px_rgba(198,255,0,0.1)]">
                {news.category || 'REPORTE ESTRATÉGICO'}
              </span>
              <div className="h-4 w-[1px] bg-white/10"></div>
              <div className="flex items-center gap-2 text-white/30 text-[10px] font-black uppercase tracking-widest">
                <Calendar size={12} />
                <span>{new Date(news.created_at).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            </motion.div>
            
            <h1 className="text-6xl sm:text-8xl lg:text-9xl font-black text-white mb-16 tracking-tighter leading-[0.85] italic uppercase">
              {news.title}
            </h1>

            {news.summary && (
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="relative max-w-3xl mx-auto pl-12 border-l border-[#C6FF00]/30 py-4"
              >
                <div className="absolute top-0 left-[-4px] w-2 h-2 rounded-full bg-[#C6FF00]"></div>
                <p className="text-2xl text-white/60 font-medium tracking-tight leading-relaxed italic text-left">
                  {news.summary}
                </p>
                <div className="absolute bottom-0 left-[-4px] w-2 h-2 rounded-full bg-[#C6FF00]"></div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Main Content Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-20">
          
          {/* Sidebar / Meta Info */}
          <aside className="lg:col-span-3 space-y-12">
            <div className="p-8 rounded-[2.5rem] bg-[#030303] border border-white/5 sticky top-32">
              <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none"></div>
              
              <div className="relative z-10 mb-10">
                <div className="text-[10px] font-black text-white/20 uppercase tracking-[0.3em] mb-4">Arquitecto</div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#C6FF00]/10 border border-[#C6FF00]/20 flex items-center justify-center font-black text-[#C6FF00] text-xl italic shadow-2xl">B</div>
                  <div>
                    <div className="text-white text-xs font-black tracking-tighter uppercase">SYSTEM ARCHITECT</div>
                    <div className="text-white/30 text-[9px] font-black uppercase tracking-widest mt-1 italic">Level 9 Access</div>
                  </div>
                </div>
              </div>

              <div className="relative z-10 mb-10">
                <div className="text-[10px] font-black text-white/20 uppercase tracking-[0.3em] mb-4">Lectura</div>
                <div className="flex items-center gap-3 text-white font-black text-sm italic tracking-tighter">
                  <Clock size={16} className="text-[#C6FF00]" />
                  <span>{Math.ceil((news.content?.length || 0) / 1000) + 2} MINUTOS</span>
                </div>
              </div>

              <div className="relative z-10 space-y-4 pt-10 border-t border-white/5">
                <div className="text-[10px] font-black text-white/20 uppercase tracking-[0.3em]">Protocolos</div>
                <div className="flex gap-3">
                  <button className="flex-1 h-12 rounded-2xl bg-white/[0.02] border border-white/5 text-white/30 hover:text-[#C6FF00] hover:border-[#C6FF00]/30 hover:bg-[#C6FF00]/5 transition-all flex items-center justify-center gap-3 group">
                    <Share2 size={16} className="group-hover:rotate-12 transition-transform" />
                    <span className="text-[9px] font-black uppercase tracking-widest">Share</span>
                  </button>
                  <button className="flex-1 h-12 rounded-2xl bg-white/[0.02] border border-white/5 text-white/30 hover:text-[#C6FF00] hover:border-[#C6FF00]/30 hover:bg-[#C6FF00]/5 transition-all flex items-center justify-center gap-3 group">
                    <Bookmark size={16} className="group-hover:scale-110 transition-all font-black" />
                    <span className="text-[9px] font-black uppercase tracking-widest">Save</span>
                  </button>
                </div>
              </div>
            </div>
          </aside>

          {/* Article Full Content */}
          <main className="lg:col-span-9">
            {news.image_url && (
              <motion.div 
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="relative mb-24 group"
              >
                <div className="absolute inset-0 bg-[#C6FF00]/10 blur-[100px] opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>
                <div className="relative aspect-[21/10] rounded-[4rem] overflow-hidden border border-white/5 bg-[#030303] shadow-[0_60px_100px_rgba(0,0,0,0.8)]">
                  <img
                    src={news.image_url}
                    alt={news.title}
                    className="w-full h-full object-cover grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-[2000ms] cubic-bezier(0.16, 1, 0.3, 1)"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
              </motion.div>
            )}

            <motion.article 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="prose prose-invert prose-2xl max-w-none"
            >
              <div 
                className="text-white/50 quill-content space-y-16" 
                dangerouslySetInnerHTML={{ __html: news.content }}
              />
              <style>{`
                .quill-content { font-size: 1.4rem; font-weight: 500; letter-spacing: -0.01em; line-height: 1.9; }
                .quill-content h1, .quill-content h2, .quill-content h3 { 
                  color: white; 
                  font-weight: 900; 
                  margin-top: 8rem;
                  margin-bottom: 2rem;
                  letter-spacing: -0.06em;
                  text-transform: uppercase;
                  font-style: italic;
                  line-height: .9;
                }
                .quill-content h1 { font-size: 5rem; }
                .quill-content h2 { font-size: 4rem; }
                .quill-content h3 { font-size: 2.8rem; }
                .quill-content p { margin-bottom: 2.5rem; text-align: justify; }
                .quill-content p strong { color: #C6FF00; font-weight: 900; }
                .quill-content ul { list-style-type: none; margin: 4rem 0; padding-left: 0; }
                .quill-content ul li { position: relative; padding-left: 3rem; margin-bottom: 1.5rem; color: rgba(255,255,255,0.7); font-size: 1.2rem; }
                .quill-content ul li::before { 
                  content: ''; 
                  position: absolute; 
                  left: 0; 
                  top: 0.85em; 
                  width: 1.5rem; 
                  height: 2px; 
                  background: #C6FF00; 
                  box-shadow: 0 0 10px rgba(198, 255, 0, 0.4);
                }
                .quill-content blockquote {
                  position: relative;
                  border-left: none;
                  padding: 6rem 4rem;
                  background: #030303;
                  border: 1px solid rgba(255,255,255,0.05);
                  border-radius: 4rem;
                  margin: 6rem 0;
                  font-style: italic;
                  font-size: 2rem;
                  font-weight: 900;
                  color: white;
                  letter-spacing: -0.05em;
                  line-height: 1.2;
                  overflow: hidden;
                }
                .quill-content blockquote::after {
                  content: '"';
                  position: absolute;
                  top: -2rem;
                  right: 2rem;
                  font-size: 15rem;
                  color: rgba(198, 255, 0, 0.05);
                  font-family: 'Outfit', sans-serif;
                }
                .quill-content a { 
                  color: #C6FF00; 
                  text-decoration: none; 
                  background: rgba(198, 255, 0, 0.05);
                  padding: 0 0.5rem;
                  border-bottom: 2px solid rgba(198, 255, 0, 0.3); 
                  font-weight: 800; 
                  transition: all 0.3s; 
                }
                .quill-content a:hover { background: #C6FF00; color: black; }
                .quill-content img { 
                  width: 100%;
                  border-radius: 4rem; 
                  margin: 8rem 0;
                  border: 1px solid rgba(255,255,255,0.05);
                  box-shadow: 0 40px 100px rgba(0,0,0,0.5);
                }
              `}</style>
            </motion.article>

            {/* Newsletter Segment */}
            <section className="mt-40 p-16 rounded-[4rem] bg-[#030303] border border-white/10 relative overflow-hidden group">
              <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none"></div>
              <div className="absolute inset-0 mesh-gradient opacity-10 group-hover:opacity-20 transition-opacity duration-1000"></div>
              
              <div className="relative z-10 text-center max-w-2xl mx-auto">
                <div className="w-20 h-20 rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-10 group-hover:border-[#C6FF00]/30 transition-colors">
                  <Megaphone size={32} className="text-[#C6FF00]" />
                </div>
                <h4 className="text-4xl sm:text-5xl font-black text-white mb-6 tracking-tighter uppercase italic">Suscríbete al Transmisor</h4>
                <p className="text-white/40 mb-12 font-medium text-lg leading-relaxed">
                  Recibe reportes de inteligencia directamente en tu terminal. Únete al 1% tecnológico.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 p-2 bg-white/5 backdrop-blur-3xl rounded-[2.5rem] border border-white/5">
                  <input 
                    type="email" 
                    placeholder="identifier@nexus.io" 
                    className="flex-grow bg-transparent border-none rounded-full px-8 py-5 text-sm font-bold tracking-tight focus:outline-none focus:ring-1 focus:ring-[#C6FF00]/30 transition-all text-white placeholder:text-white/10"
                  />
                  <button className="px-12 py-5 bg-[#C6FF00] text-black font-black uppercase tracking-[0.2em] text-[10px] rounded-[2rem] hover:scale-[1.02] active:scale-95 transition-all shadow-[0_20px_40px_rgba(198,255,0,0.2)]">
                    Inyectar Datos
                  </button>
                </div>
              </div>
            </section>

            {/* More Signals Section (Related Articles) */}
            <section className="mt-40">
              <h2 className="text-5xl font-black text-white mb-16 tracking-tighter uppercase italic text-center">Más Señales</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                {/* Placeholder for related articles */}
                {[1, 2].map((item, index) => (
                  <motion.article
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.8 }}
                    className="group flex flex-col"
                  >
                    <Link to={`/noticias/${item}`} className="block overflow-hidden rounded-[2.5rem] bg-black border border-white/5 mb-8 aspect-[16/10] relative group-hover:border-[#C6FF00]/30 transition-all duration-500">
                      <div className="w-full h-full flex items-center justify-center mesh-gradient opacity-10">
                        <Clock size={48} className="text-white/20" />
                      </div>
                      <div className="absolute top-6 left-6">
                        <span className="px-4 py-1.5 rounded-full bg-black/80 backdrop-blur-xl border border-white/10 text-[9px] text-white/80 font-black uppercase tracking-[0.2em]">
                          Despliegue
                        </span>
                      </div>
                    </Link>
                    
                    <div className="flex items-center gap-4 mb-6">
                      <span className="text-[#C6FF00] font-black text-[10px] uppercase tracking-widest">
                        {new Date().toLocaleDateString('es-ES', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                      <span className="w-1 h-1 rounded-full bg-white/20"></span>
                      <span className="text-white/30 text-[10px] font-black uppercase tracking-widest">5 min lect</span>
                    </div>
                    
                    <h3 className="text-3xl sm:text-4xl font-black text-white mb-6 group-hover:text-[#C6FF00] transition-colors line-clamp-2 leading-[1.1] tracking-tighter">
                      <Link to={`/noticias/${item}`}>Título de Señal Relacionada {item}</Link>
                    </h3>
                    
                    <p className="text-white/40 text-lg font-medium tracking-tight mb-8 line-clamp-2 leading-relaxed">
                      Un breve resumen de esta señal relacionada, ofreciendo una perspectiva adicional sobre temas clave.
                    </p>
                    
                    <Link
                      to={`/noticias/${item}`}
                      className="mt-auto inline-flex items-center space-x-4 text-white font-black text-xs uppercase tracking-[0.3em] group/btn transition-all"
                    >
                      <span className="group-hover/btn:text-[#C6FF00] transition-colors">Leer Señal Completa</span>
                      <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center group-hover/btn:border-[#C6FF00] transition-colors">
                        <ArrowLeft size={14} className="rotate-180 group-hover/btn:translate-x-1 transition-transform" />
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            </section>
          </main>
        </div>
      </div>

      {/* Footer Navigation */}
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 border-t border-white/5">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-12">
          <Link to="/noticias" className="group flex items-center gap-6">
            <div className="w-14 h-14 rounded-full border border-white/10 flex items-center justify-center text-white/30 group-hover:border-[#C6FF00] group-hover:text-[#C6FF00] transition-colors">
              <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            </div>
            <div className="text-left">
              <div className="text-white/20 text-[10px] font-black uppercase tracking-widest">Siguiente Señal</div>
              <div className="text-white text-lg font-black tracking-tighter uppercase italic group-hover:text-[#C6FF00] transition-colors">Regresar al Archivo Central</div>
            </div>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="w-1 h-1 rounded-full bg-[#C6FF00]"></div>
            <div className="text-white font-black text-[10px] uppercase tracking-[0.4em]">Ecosistema Build Brains</div>
            <div className="w-1 h-1 rounded-full bg-[#C6FF00]"></div>
          </div>
        </div>
      </footer>
    </div>
  );
};